import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { KYCModalModeTypes, localStorageKeysConstants } from '../../constants';
import { useIsAuthenticated, useIsPsd2ObAvailable } from '../../hooks';
import CompanyInviteModal from '../quickStart/CompanyInviteModal/CompanyInviteModal';
import EmailVerificationModal from '../quickStart/EmailVerificationModal/EmailVerificationModal';
import CompanyDisableModal from '../quickStart/CompanyDisableModal/CompanyDisableModal';
import LightKYCModal from '../quickStart/LightKYCModal/LightKYCModal';
import WelcomeModal from '../quickStart/WelcomeModal';
import KYCModal from '../quickStart/KYCModal/KYCModal';
import TermsAcceptanceModal from '../quickStart/TermsAcceptanceModal';
import ConnectAccountModal from '../pages/TransactionsPage/ConnectAccountModal';
import FullWidthAgentChat from '../FullWidthAgentChat';

import { mainActions } from '../../state/actions/main.actions';
import { userActions } from '../../state/actions/user.actions';
const {SHOW_WELCOME} = localStorageKeysConstants;

const SystemModals = () => {
  const dispatch = useDispatch();

  const companyInviteToken = useSelector(state => state.auth.companyInviteToken);
  const disabledCompany = useSelector(state => state.user.disabledCompany);
  const emailVerificationToken = useSelector(state => state.auth.emailVerificationToken);
  const isAuthenticated = useIsAuthenticated();
  const showWelcome = useSelector(state => state.auth.showWelcome);

  const isPsd2ObAvailable = useIsPsd2ObAvailable();

  const [isOpenCompanyInviteModal, setIsOpenCompanyInviteModal] = useState(false);
  const [isOpenWelcomeModal, setIsOpenWelcomeModal] = useState(false);
  const [isOpenVerificationModal, setIsOpenVerificationModal] = useState(false);
  const [companyDisableModalProps, setCompanyDisableModalProps] = useState({open: false, company: null});

  const showWelcomeModal = useMemo(() => {
    return !isOpenVerificationModal && isAuthenticated && isOpenWelcomeModal && !isOpenCompanyInviteModal;
  }, [isAuthenticated, isOpenVerificationModal, isOpenWelcomeModal, isOpenCompanyInviteModal])

  useEffect(() => {
    if (emailVerificationToken) setIsOpenVerificationModal(true);
  }, [emailVerificationToken]);

  useEffect(() => {
    if (disabledCompany) {
      const {isDisabled, company} = disabledCompany;
      setCompanyDisableModalProps({...companyDisableModalProps, open: isDisabled, company});
    }
  }, [disabledCompany]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showWelcome) setTimeout(() => setIsOpenWelcomeModal(true), 500);
  }, [showWelcome]);

  useEffect(() => {
    if (companyInviteToken) setIsOpenCompanyInviteModal(true);
  }, [companyInviteToken]);

  useEffect(() => {
    if (isAuthenticated) {
      if (localStorage.getItem(SHOW_WELCOME) === 'true') {
        setIsOpenWelcomeModal(true);
      }

      dispatch(userActions.getCompanies(
        (company) => dispatch(userActions.disableEmployeeFromCompany(true, company)),
        (data) => !Boolean(data.length) && setIsOpenWelcomeModal(true)
      ));
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps


  const removeShowVerification = () => {
    setIsOpenWelcomeModal(false);
    localStorage.removeItem(SHOW_WELCOME);
  }

  const handleCloseCompanyInviteModal = () => {
    setIsOpenCompanyInviteModal(false);
    removeShowVerification();
  }

  const handleCancelVerificationModal = () => {
    setIsOpenVerificationModal(false);
    removeShowVerification();
  }

  const handleCloseDisableModal = () => {
    setCompanyDisableModalProps({...companyDisableModalProps, open: false});
    dispatch(userActions.disableEmployeeFromCompany());
  }

  const handleOkDisableModal = () => {
    handleCloseDisableModal();
    dispatch(mainActions.openKycWindow({closeIcon: false}));
  }

  const handleOkWelcomeModal = () => {
    setIsOpenWelcomeModal(false);
    dispatch(mainActions.openKycWindow({mode: KYCModalModeTypes.COMPANY}));
  }

  const handleOkCompanyCreationModal = (createdCompanyData) => {
    const {id: companyId, isNeedToChange} = createdCompanyData;
    if (isNeedToChange) dispatch(userActions.changeCompany(companyId));
  }

  return (
    <>
      {isPsd2ObAvailable && <ConnectAccountModal />}

      {/*
        Deliberately false, and NOT the same switch as the one in the navigation. Self-serve signup
        is off: a company is created by backoffice-support staff, never by the person landing here.
        With false this modal shows "waitInvitation" and no button, which is the intended dead end.
        Turning it on would hand company creation to anyone who reaches the welcome screen.
      */}
      <WelcomeModal
        enabledCompanyCreation={false}
        open={showWelcomeModal}
        handleOk={handleOkWelcomeModal}
      />

      {/*
        Renders nothing at all unless this particular person still owes an acceptance, so it is safe
        to mount unconditionally — the decision lives inside the component, next to the two profile
        responses it depends on (H1MVP-4654).
      */}
      <TermsAcceptanceModal />

      <LightKYCModal />

      <KYCModal onOk={handleOkCompanyCreationModal} />

      <EmailVerificationModal
        open={isOpenVerificationModal}
        handleCancel={handleCancelVerificationModal}
      />

      <CompanyInviteModal
        open={isOpenCompanyInviteModal}
        handleClose={handleCloseCompanyInviteModal}
      />

      <CompanyDisableModal
        {...companyDisableModalProps}
        handleClose={handleCloseDisableModal}
        handleOk={handleOkDisableModal}
      />

      <FullWidthAgentChat />
    </>
  );
};

export default SystemModals;
